import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { CalendarDays, Printer } from "lucide-react";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { DatePicker } from "@/components/ui/DatePicker";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { countWorkingDays, buildCompanyHolidaySet } from "@/utils/workingDays";
import { saveBlobResponse } from "@/utils/download";
import { leavesApi } from "../api/leaves";
import { useCreateLeaveRequest } from "../hooks/useLeaves";
import { type LeaveType } from "../types";

const LEAVE_TYPES: { value: LeaveType; label: string }[] = [
  { value: "annual", label: "Urlop wypoczynkowy" },
  { value: "circumstantial", label: "Urlop okolicznościowy" },
  { value: "rehabilitation", label: "Urlop rehabilitacyjny" },
  { value: "care", label: "Urlop opiekuńczy" },
  { value: "training", label: "Urlop szkoleniowy" },
  { value: "other", label: "Urlop inny" },
];

const HOURS_OPTIONS = [0, 1, 2, 3, 4, 5, 6, 7];
const MINUTES_OPTIONS = [0, 15, 30, 45];

interface Props {
  open: boolean;
  onClose: () => void;
  employee: { id: string; name: string; contractType: string | null };
}

/**
 * Lets a reviewer generate and submit a leave request on behalf of `employee`.
 * Mirrors the self-service form on /leaves, but the request is filed for the
 * chosen employee (the backend records the reviewer as `submitted_by`).
 */
export function ProxyLeaveSubmitModal({ open, onClose, employee }: Props) {
  const createLeave = useCreateLeaveRequest();

  const [leaveType, setLeaveType] = useState<LeaveType>("annual");
  const [isHourly, setIsHourly] = useState(false);
  const [otherType, setOtherType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [hoursCount, setHoursCount] = useState(2);
  const [minutesCount, setMinutesCount] = useState(0);
  const [note, setNote] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isB2b = employee.contractType === "b2b";
  const entitlementYear = startDate ? parseInt(startDate.slice(0, 4)) : new Date().getFullYear();
  const showEntitlement = leaveType === "annual";

  const { data: companyHolidays = [] } = useQuery({
    queryKey: ["company-holidays"],
    queryFn: () => leavesApi.getCompanyHolidays(),
    staleTime: 1000 * 60 * 60,
  });
  const companyHolidayDates = useMemo(
    () => buildCompanyHolidaySet(companyHolidays),
    [companyHolidays],
  );
  const { data: entitlement } = useQuery({
    queryKey: ["leave-entitlement", employee.id, entitlementYear],
    queryFn: () => leavesApi.getEntitlement(entitlementYear, employee.id),
    enabled: open && showEntitlement,
    staleTime: 1000 * 60 * 5,
  });

  const workingDaysPreview = useMemo(
    () => (startDate && endDate && endDate >= startDate
      ? countWorkingDays(startDate, endDate, companyHolidayDates)
      : null),
    [startDate, endDate, companyHolidayDates],
  );

  const reset = () => {
    setLeaveType("annual");
    setIsHourly(false);
    setOtherType("");
    setStartDate("");
    setEndDate("");
    setHoursCount(2);
    setMinutesCount(0);
    setNote("");
    setFile(null);
    setErrors({});
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (isHourly) {
      if (!startDate) e.startDate = "Wymagane";
      const total = hoursCount * 60 + minutesCount;
      if (total < 1 || total >= 480) e.hoursCount = "Łączny czas musi wynosić od 1 minuty do 7h 59min";
      if (!isB2b && !file) e.file = "Skan wniosku jest wymagany";
      return e;
    }
    if (!startDate) e.startDate = "Wymagane";
    if (!endDate) e.endDate = "Wymagane";
    if (startDate && endDate && endDate < startDate) e.endDate = "Data końcowa nie może być przed datą początkową";
    if (startDate && endDate && startDate.slice(0, 4) !== endDate.slice(0, 4))
      e.endDate = "Wniosek nie może obejmować dwóch różnych lat kalendarzowych";
    if (leaveType === "other" && !otherType.trim()) e.otherType = "Należy podać rodzaj urlopu";
    if (!isB2b && !file) e.file = "Skan wniosku jest wymagany";
    return e;
  };

  const submit = () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }

    const fd = new FormData();
    fd.append("employee", employee.id);
    fd.append("leave_type", leaveType);
    fd.append("is_hourly", isHourly ? "true" : "false");
    if (isHourly) {
      fd.append("start_date", startDate);
      fd.append("end_date", startDate);
      fd.append("hours_count", String(hoursCount));
      fd.append("minutes_count", String(minutesCount));
    } else {
      if (leaveType === "other") fd.append("other_type", otherType.trim());
      fd.append("start_date", startDate);
      fd.append("end_date", endDate);
    }
    if (file) fd.append("attachment", file);
    fd.append("note", note);

    createLeave.mutate(fd, {
      onSuccess: () => { handleClose(); },
      onError: (err: unknown) => {
        const detail = (err as { response?: { data?: { errors?: { detail?: string } } } })
          ?.response?.data?.errors?.detail;
        setErrors({ submit: detail ?? "Błąd podczas składania wniosku. Spróbuj ponownie." });
      },
    });
  };

  return (
    <Modal open={open} onClose={handleClose} title={`Złóż wniosek urlopowy — ${employee.name}`}>
      <div className="flex flex-col gap-4">
        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
            Rodzaj urlopu
          </label>
          <select
            className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
            value={leaveType}
            onChange={(e) => { setLeaveType(e.target.value as LeaveType); setOtherType(""); setStartDate(""); setEndDate(""); }}
          >
            {LEAVE_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <label className="mt-2 flex items-center gap-2 cursor-pointer select-none w-fit">
            <input
              type="checkbox"
              className="w-4 h-4 rounded accent-[var(--color-primary)]"
              checked={isHourly}
              onChange={(e) => { setIsHourly(e.target.checked); setStartDate(""); setEndDate(""); }}
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Urlop godzinowy</span>
          </label>
          {showEntitlement && entitlement && (
            <div className={`mt-2 flex items-center gap-2 text-sm px-3 py-2 rounded-md border ${
              entitlement.available_days <= 0 && entitlement.available_hours_remainder <= 0 && entitlement.available_minutes_remainder <= 0
                ? "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400"
                : "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-400"
            }`}>
              <CalendarDays size={14} className="flex-shrink-0" />
              <span>
                Dostępne:{" "}
                <strong>{entitlement.available_days}</strong>
                {entitlement.available_days === 1 ? " dzień" : " dni"}
                {entitlement.available_hours_remainder > 0 && (
                  <span> i <strong>{entitlement.available_hours_remainder}h</strong></span>
                )}
                {entitlement.available_minutes_remainder > 0 && (
                  <span> <strong>{entitlement.available_minutes_remainder}min</strong></span>
                )}
                {entitlement.prev_year_carryover > 0 && (
                  <span className="text-xs ml-1 opacity-70">
                    (w tym {entitlement.prev_year_carryover} z poprzedniego roku)
                  </span>
                )}
              </span>
            </div>
          )}
        </div>
        {leaveType === "other" && (
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Jaki rodzaj urlopu? <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              value={otherType}
              onChange={(e) => setOtherType(e.target.value)}
              placeholder="np. urlop bezpłatny, urlop wychowawczy..."
            />
            {errors.otherType && <p className="text-xs text-red-500 mt-1">{errors.otherType}</p>}
          </div>
        )}
        {isHourly ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Data urlopu <span className="text-red-500">*</span>
              </label>
              <DatePicker
                value={startDate}
                onChange={setStartDate}
              />
              {errors.startDate && <p className="text-xs text-red-500 mt-1">{errors.startDate}</p>}
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Godziny <span className="text-red-500">*</span>
              </label>
              <select
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                value={hoursCount}
                onChange={(e) => setHoursCount(parseInt(e.target.value))}
              >
                {HOURS_OPTIONS.map((h) => (
                  <option key={h} value={h}>{h}h</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Minuty
              </label>
              <select
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                value={minutesCount}
                onChange={(e) => setMinutesCount(parseInt(e.target.value))}
              >
                {MINUTES_OPTIONS.map((m) => (
                  <option key={m} value={m}>{m}min</option>
                ))}
              </select>
              {errors.hoursCount && <p className="text-xs text-red-500 mt-1">{errors.hoursCount}</p>}
            </div>
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Zakres dat <span className="text-red-500">*</span>
              </label>
              {workingDaysPreview !== null && (
                <span className="text-xs font-semibold text-[var(--color-primary)] bg-[var(--color-primary-dim)] dark:bg-[var(--color-primary-hover)] px-2 py-0.5 rounded-full">
                  {workingDaysPreview} {workingDaysPreview === 1 ? "dzień roboczy" : "dni roboczych"}
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <DatePicker
                  value={startDate}
                  onChange={setStartDate}
                />
                {errors.startDate && <p className="text-xs text-red-500 mt-1">{errors.startDate}</p>}
              </div>
              <div>
                <DatePicker
                  value={endDate}
                  onChange={setEndDate}
                  min={startDate}
                />
                {errors.endDate && <p className="text-xs text-red-500 mt-1">{errors.endDate}</p>}
              </div>
            </div>
          </div>
        )}
        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
            Uwagi <span className="text-gray-400 text-xs">(opcjonalne)</span>
          </label>
          <textarea
            rows={2}
            className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Dodatkowe informacje..."
          />
        </div>
        {!isB2b && (
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Skan podpisanego wniosku <span className="text-red-500">*</span>
            </label>
            <FileDropZone
              files={file ? [file] : []}
              onFilesChange={(fs) => setFile(fs[0] ?? null)}
              accept=".pdf,.jpg,.jpeg,.png"
            />
            {errors.file && <p className="text-xs text-red-500 mt-1">{errors.file}</p>}
          </div>
        )}
        {errors.submit && <p className="text-sm text-red-600">{errors.submit}</p>}
        <div className="flex gap-3 flex-wrap">
          <Button loading={createLeave.isPending} onClick={submit}>
            Złóż wniosek
          </Button>
          {!isB2b && !isHourly && (
            <Button
              variant="warning"
              disabled={!startDate || !endDate || endDate < startDate}
              onClick={async () => {
                const resp = await leavesApi.generatePdf({
                  leave_type: leaveType,
                  other_type: leaveType === "other" ? otherType : undefined,
                  start_date: startDate,
                  end_date: endDate,
                  days_count: workingDaysPreview ?? 0,
                  employee: employee.id,
                });
                saveBlobResponse(resp, `wniosek_urlopowy_${startDate}.pdf`);
              }}
            >
              <Printer size={14} className="mr-1.5" /> Generuj PDF
            </Button>
          )}
          {!isB2b && isHourly && (
            <Button
              variant="warning"
              disabled={!startDate || hoursCount * 60 + minutesCount < 1}
              onClick={async () => {
                const resp = await leavesApi.generatePdf({
                  leave_type: leaveType,
                  other_type: leaveType === "other" ? otherType : undefined,
                  start_date: startDate,
                  end_date: startDate,
                  days_count: 0,
                  is_hourly: true,
                  hours_count: hoursCount,
                  minutes_count: minutesCount,
                  employee: employee.id,
                });
                saveBlobResponse(resp, `wniosek_urlopowy_${startDate}.pdf`);
              }}
            >
              <Printer size={14} className="mr-1.5" /> Generuj PDF
            </Button>
          )}
          <Button variant="secondary" onClick={handleClose}>Anuluj</Button>
        </div>
      </div>
    </Modal>
  );
}
